import { useDebounce } from '@uidotdev/usehooks'
import { Modal } from 'antd'
import { AxiosError } from 'axios'
import { Suspense, useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'sonner'
import { ZodError } from 'zod'

import { DELAY, OWNER_ROLES } from '@/constants'
import useSearch from '@/hooks/search/useSearch'
import { ScreenSizeContext } from '@/providers/ScreenSizeProvider'
import { organisationDtoSchema } from '@/schemas'
import { useFeedStore } from '@/stores/feed/feedStore'
import { useSearchStore } from '@/stores/feed/searchStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type { OrganisationInterface } from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { safeLazy } from '@/utils/app/safeLazy'
import { selectedOrganisationCodeCryptographer } from '@/utils/auth/selectedOrganisationCodeCryptographer'
import zodErrorModalMessage from '@/utils/forms/zodErrorModalMessage'

import AllOrganisations from './AllOrganisations/AllOrganisations'
import ClientActions from './ClientActions/ClientActions'
import OrganisationCreation from './OrganisationCreation/OrganisationCreation'
import {
  getIsVatNumber,
  ORGANISATIONS_DEFAULT_PAGE_SIZE,
} from './OrganisationsSearch.config'
import cn from './OrganisationsSearch.module.scss'
import OrganisationsSearchResults from './OrganisationsSearchResults/OrganisationsSearchResults'

const AllOrganisationsBySearch = safeLazy(
  () => import('./AllOrganisationsBySearch/AllOrganisationsBySearch')
)

interface OrganisationsSearchProps {
  variant?: 'bar' | 'icons'
}

function OrganisationsSearch({ variant = 'bar' }: OrganisationsSearchProps) {
  const [organisationsBySearch, setOrganisationsBySearch] = useState<
    OrganisationInterface[]
  >([])
  const [
    totalOrganisationsBySearchNumber,
    setTotalOrganisationsBySearchNumber,
  ] = useState<number>(0)
  const [openShowMoreOrganisationsModal, setOpenShowMoreOrganisationsModal] =
    useState<boolean>(false)
  const [search, setSearch] = useState<string>('')
  const [searchInputFocused, setSearchInputFocused] = useState<boolean>(false)
  const [zodErrorExists, setZodErrorExists] = useState<boolean>(false)
  const [axiosErrorExists, setAxiosErrorExists] = useState<boolean>(false)

  const screen = useContext(ScreenSizeContext)
  const location = useLocation()

  const debouncedSearch = useDebounce(search, DELAY)

  const {
    setSelectedOrganisation,
    selectedOrganisation,
    tokenRoles,
    setIsOrgSwitching,
    setUserContext,
  } = useUserInfoStore((state) => state)

  const {
    searchTerm,
    currentlySearchableCategories,
    currentlySearchableBrands,
    currentlySearchableCharacteristics,
    withLeftovers,
  } = useSearchStore((state) => state)

  const { setProductInfoToCartPresence } = useFeedStore((state) => state)

  const [modal] = Modal.useModal()

  const searchProducts = useSearch()

  useEffect(() => {
    const value = debouncedSearch

    if (!searchInputFocused) {
      return
    }

    if (debouncedSearch && !zodErrorExists && !axiosErrorExists) {
      api
        .get(`/organisations`, {
          useTimeout: false,
          params: {
            ...(getIsVatNumber(value) && { vatNumber: value }),
            ...(!getIsVatNumber(value) && { description: value }),
            pageSize: ORGANISATIONS_DEFAULT_PAGE_SIZE,
          },
        })
        .then((response) => {
          const orgData = response.data.organisations
          const content = orgData?.content || []
          const validatedOrganisations: OrganisationInterface[] =
            organisationDtoSchema.array().parse(content)

          if (searchInputFocused) {
            setOrganisationsBySearch(validatedOrganisations)
            setTotalOrganisationsBySearchNumber(orgData?.totalElements || 0)
          }
        })
        .catch((error) => {
          if (error instanceof AxiosError || error instanceof ZodError) {
            setOrganisationsBySearch([])
            setTotalOrganisationsBySearchNumber(0)
          }
          if (error instanceof AxiosError) {
            handleApiError(error, modal)
            setAxiosErrorExists(true)
          }
          if (error instanceof ZodError) {
            zodErrorModalMessage(modal)
            setZodErrorExists(true)
          }
        })
    } else {
      setOrganisationsBySearch([])
      setTotalOrganisationsBySearchNumber(0)
    }
  }, [
    debouncedSearch,
    searchInputFocused,
    modal,
    zodErrorExists,
    axiosErrorExists,
  ])

  const handleChooseOrganisation = async (
    organisation: OrganisationInterface
  ) => {
    setIsOrgSwitching(true)
    const loadingToast = toast.loading('Выбор клиента...')

    try {
      localStorage.setItem('currentClientId', organisation.id)

      localStorage.setItem(
        'selectedOrganisationCode',
        selectedOrganisationCodeCryptographer.encrypt(organisation.code)
      )

      const response = await api.get('/context', {
        params: { clientId: organisation.id },
      })

      const contextData = response.data.context

      setUserContext(contextData)
      setSelectedOrganisation(contextData.currentClient || {})

      toast.success('Клиент выбран', {
        id: loadingToast,
        description: organisation.description,
      })

      setProductInfoToCartPresence({})

      setTimeout(() => {
        setSearch('')
      }, 1000)

      setOpenShowMoreOrganisationsModal(false)

      if (location.pathname.includes('/feed')) {
        await searchProducts({
          query: searchTerm,
          categories: currentlySearchableCategories,
          brands: currentlySearchableBrands,
          page: 0,
          characteristicFilters: currentlySearchableCharacteristics,
          withLeftovers: withLeftovers,
          clientId: organisation.id,
        })
      }
    } catch (error) {
      toast.dismiss(loadingToast)

      localStorage.removeItem('currentClientId')
      localStorage.removeItem('selectedOrganisationCode')

      if (error instanceof ZodError) {
        zodErrorModalMessage(modal)
      } else {
        handleApiError(error, modal)
      }
    } finally {
      setIsOrgSwitching(false)
    }
  }

  const isWideScreen = [screen?.lg, screen?.xl, screen?.md].some(Boolean)
  const canCreateOrganisation = !tokenRoles.includes(OWNER_ROLES.ADMIN)

  const resetSearchResults = () => {
    setOrganisationsBySearch([])
    setTotalOrganisationsBySearchNumber(0)
  }

  const searchInput = (
    <input
      placeholder="Введите клиента..."
      className={cn.input}
      value={search}
      readOnly={Object.keys(selectedOrganisation).length > 0}
      onClick={() => {
        if (Object.keys(selectedOrganisation).length > 0) {
          toast.warning('Отмените текущего клиента', {
            description:
              'Пожалуйста, отмените выбранную организацию перед выбором новой',
          })
        }
      }}
      onFocus={() => {
        setSearchInputFocused(true)
      }}
      onChange={(e) => setSearch(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          resetSearchResults()
          toast.info('Поиск сброшен')
        }
      }}
      onBlur={() =>
        setTimeout(() => {
          resetSearchResults()
          setSearchInputFocused(false)
        }, 500)
      }
    />
  )

  const searchResults = (
    <OrganisationsSearchResults
      organisations={organisationsBySearch}
      totalCount={totalOrganisationsBySearchNumber}
      search={search}
      isFocused={searchInputFocused}
      onChoose={handleChooseOrganisation}
      onShowMore={() => setOpenShowMoreOrganisationsModal(true)}
    />
  )

  const showMoreModal = openShowMoreOrganisationsModal && (
    <Suspense fallback={null}>
      <AllOrganisationsBySearch
        debouncedSearch={debouncedSearch}
        totalOrganisationsBySearchNumber={totalOrganisationsBySearchNumber}
        openShowMoreOrganisationsModal={openShowMoreOrganisationsModal}
        handleChooseOrganisation={handleChooseOrganisation}
        setOpenShowMoreOrganisationsModal={setOpenShowMoreOrganisationsModal}
      />
    </Suspense>
  )

  if (variant === 'icons') {
    return (
      <>
        <ClientActions
          isWideScreen={isWideScreen}
          canCreateOrganisation={canCreateOrganisation}
          searchInput={searchInput}
          searchResults={searchResults}
          onChooseOrganisation={handleChooseOrganisation}
        />
        {showMoreModal}
      </>
    )
  }

  return (
    <>
      {!isWideScreen && (
        <AllOrganisations handleChooseOrganisation={handleChooseOrganisation} />
      )}
      {isWideScreen && (
        <div className={cn.organisationsSearch}>
          <div className={cn.inputContainer}>{searchInput}</div>
          <div className={cn.buttons}>
            <AllOrganisations
              handleChooseOrganisation={handleChooseOrganisation}
            />
            {canCreateOrganisation && <OrganisationCreation />}
          </div>
          {searchResults}
          {showMoreModal}
        </div>
      )}
    </>
  )
}

export default OrganisationsSearch
